import React, { lazy, memo, Suspense, useState } from 'react';
import { Button, Dropdown, message } from 'antd';
import { AssetItemDto } from '@api/Api';
import { download } from '@helpers/download';
import {
  ActionIcon,
  DeleteIcon,
  DownloadInvoiceIcon,
  SuccessIcon
} from '@icons';
import {
  QUERY_ASSET_REVIEW_CONTENT,
  useUpdateAssetReviewMutation
} from '@hooks/queries/assetReview';

const ConfirmModal = lazy(() => import('@components/Modals/ConfirmModal'));

type ActionAssetProps = {
  asset: AssetItemDto;
  canManageReviews: boolean;
  reviewId: string;
};

export default memo(function ActionAsset({
  asset,
  canManageReviews,
  reviewId
}: ActionAssetProps) {
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const canDownload = !!asset.asset?.permissions.download;

  const { mutateAsync: updateAssetReview, isPending: isUpdatingAssetReview } =
    useUpdateAssetReviewMutation({
      queryKey: [QUERY_ASSET_REVIEW_CONTENT]
    });

  const onRemoveAssetFromReview = async () => {
    try {
      setIsShowConfirmModal(false);
      await updateAssetReview({
        reviewId,
        assets: {
          remove: [asset.id]
        }
      });
      message.success({
        key: 'asset-removed-from-review',
        content: `${asset.asset?.versions[0].name} has been removed from the shared link.`,
        className: 'message-dark-modal',
        icon: <SuccessIcon className="anticon" />,
        duration: 2
      });
    } catch (error) {
      console.error(error);
    }
  };

  if (canManageReviews) {
    return (
      <>
        <Dropdown
          menu={{
            onClick: ({ domEvent }) => domEvent.stopPropagation(),
            items: [
              ...(canDownload
                ? [
                    {
                      key: 'download',
                      icon: <DownloadInvoiceIcon />,
                      label: 'Download',
                      onClick: () => {
                        if (!asset.asset) return;
                        const { name, sourceUrl } = asset.asset.versions[0];
                        download(sourceUrl, name);
                      }
                    }
                  ]
                : []),
              {
                key: 'delete',
                icon: <DeleteIcon />,
                label: 'Remove from shared link',
                onClick: () => {
                  setIsShowConfirmModal(true);
                }
              }
            ]
          }}
          overlayClassName="asset_links_overlay"
          placement="bottomLeft"
          trigger={['click']}
        >
          <Button
            className="asset_links_action"
            onClick={(e) => e.stopPropagation()}
            type="text"
            size="small"
            loading={isUpdatingAssetReview}
            style={{
              width: 24,
              height: 24,
              marginLeft: 'auto'
            }}
          >
            <ActionIcon />
          </Button>
        </Dropdown>
        <Suspense fallback={null}>
          <div onClick={(e) => e.stopPropagation()}>
            <ConfirmModal
              visible={isShowConfirmModal}
              onConfirm={onRemoveAssetFromReview}
              onCancel={() => setIsShowConfirmModal(false)}
              okText="Remove"
              title="Remove media from shared link"
              description="Are you sure you want to remove this media from the shared link?"
            />
          </div>
        </Suspense>
      </>
    );
  }

  if (!canDownload) return null;

  return (
    <Button
      type="text"
      size="small"
      className="campaign-review-btn-download"
      icon={<DownloadInvoiceIcon />}
      onClick={(e) => {
        e.stopPropagation();
        if (!asset.asset) return;
        const { name, sourceUrl } = asset.asset.versions[0];
        download(sourceUrl, name);
      }}
    />
  );
});
